import { computed, inject, Injectable } from '@angular/core';
import { NAVIGATOR, WINDOW } from '../tokens';
import { toSignal } from '@angular/core/rxjs-interop';
import { fromEvent, map, shareReplay } from 'rxjs';
import type { Breakpoint } from '../models';

@Injectable({
  providedIn: 'root',
})
export class DeviceHelperService {
  readonly #window = inject(WINDOW);
  readonly #navigator = inject(NAVIGATOR);

  isTouchDevice = (): boolean => {
    return 'ontouchstart' in this.#window || this.#navigator.maxTouchPoints > 0;
  };

  isLessThanSM = computed(() => ['xs'].includes(this.#breakpoint()));
  isLessThanMD = computed(() => ['xs', 'sm'].includes(this.#breakpoint()));
  isLessThanLG = computed(() =>
    ['xs', 'sm', 'md'].includes(this.#breakpoint()),
  );
  isLessThanXL = computed(() =>
    ['xs', 'sm', 'md', 'lg'].includes(this.#breakpoint()),
  );

  isSM = computed(() => ['sm', 'md', 'lg', 'xl'].includes(this.#breakpoint()));
  isMD = computed(() => ['md', 'lg', 'xl'].includes(this.#breakpoint()));
  isLG = computed(() => ['lg', 'xl'].includes(this.#breakpoint()));
  isXL = computed(() => ['xl'].includes(this.#breakpoint()));

  #breakpoint = toSignal(
    fromEvent(this.#window, 'resize').pipe(
      map(() => this.calculateBreakpoint()),
      shareReplay({ refCount: true, bufferSize: 1 }),
    ),
    { initialValue: this.calculateBreakpoint() },
  );

  private calculateBreakpoint() {
    let breakpoint: Breakpoint = 'xs';
    const windowWidth = this.#window.innerWidth;
    if (windowWidth >= 1280) {
      breakpoint = 'xl';
    } else if (windowWidth >= 1024) {
      breakpoint = 'lg';
    } else if (windowWidth >= 768) {
      breakpoint = 'md';
    } else if (windowWidth >= 640) {
      breakpoint = 'sm';
    }
    return breakpoint;
  }
}
